// ShortcutHelp — the keyboard-shortcut help window (Wave 2C).
//
// A read-only WINDOW listing src/lib/shortcutRegistry.js grouped by scope.
// Open via `?` or Ctrl/⌘+/ (App.jsx's global keydown, guarded against
// text-entry targets) or View ▸ "Keyboard Shortcuts". Esc closes it.
//
// A FloatingWindow, like SettingsWorkspace — which is what this file's header
// always claimed ("same overlay pattern as SettingsWorkspace"), except Settings
// became a window and this stayed a modal behind a scrim. It is a reference you
// read WHILE trying the shortcuts it lists, so a backdrop that blocks the app is
// exactly wrong: now it can be dragged aside, resized, or popped onto a second
// monitor while you work. `open`/`onClose` props and `if (!open) return null`
// are unchanged, as is its document-level Esc listener. Mounted in App's
// menubarPanels wrapper so it survives every render branch.

import React, { useEffect, useMemo } from 'react'

import FloatingWindow from './FloatingWindow.jsx'

import { MONO, UI_FONT } from './PipelineGlyph.jsx'
import { card, head } from '../lib/studioStyles.js'
import {
  ALL_SHORTCUTS, SCOPE_ORDER, formatKeyList, detectPlatform,
} from '../lib/shortcutRegistry.js'

export default function ShortcutHelp({ open, onClose }) {
  // Close on Escape for keyboard parity with the app's other overlays.
  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const platform = useMemo(() => detectPlatform(), [])
  const groups = useMemo(() => SCOPE_ORDER
    .map(scope => [scope, ALL_SHORTCUTS.filter(e => e.scope === scope)])
    .filter(([, entries]) => entries.length > 0), [])

  if (!open) return null

  return (
    <FloatingWindow
      id="shortcut-help"
      title="KEYBOARD SHORTCUTS"
      onClose={onClose}
      dockable={false}
      defaultSize={{ w: 560, h: 620 }}
    >
      <div
        data-testid="shortcut-help"
        // Still a dialog to a screen reader, and still needs its own name — the
        // window bar's title is the sighted label, and dropping these with the
        // scrim left the surface unnamed. NOT aria-modal: it no longer traps
        // the app, which is the whole point of the conversion.
        role="dialog"
        aria-label="Keyboard shortcuts"
        className="aos-thin-scroll"
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          background: 'var(--m-panel)', color: 'var(--m-fg)',
          ...card,
          border: 'none', borderRadius: 0, boxShadow: 'none',
          padding: '14px 18px 16px',
        }}
      >
        {/* The window bar carries the title now — this row keeps only the
            hint, which is about behaviour rather than identity. */}
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 6 }}>
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: UI_FONT, fontSize: 9, color: 'var(--m-fg-3)' }}>
            Esc to close
          </span>
        </div>

        {groups.map(([scope, entries]) => (
          <div key={scope}>
            <div style={{
              ...head, padding: '10px 0 4px',
              borderBottom: '1px solid var(--m-line)', marginBottom: 2,
            }}>{scope.toUpperCase()}</div>
            {entries.map(entry => (
              <div
                key={entry.id}
                data-testid={`shortcut-help-row-${entry.id}`}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 12,
                  padding: '4px 2px', fontSize: 11.5, color: 'var(--m-fg-2)',
                }}
              >
                <span>{entry.label}</span>
                {entry.when && (
                  <span style={{ fontFamily: UI_FONT, fontSize: 8.5, color: 'var(--m-fg-3)' }}>
                    ({entry.when})
                  </span>
                )}
                <span style={{ flex: 1 }} />
                <code style={{
                  fontFamily: MONO, fontSize: 10.5, whiteSpace: 'nowrap',
                  color: 'var(--m-fg)',
                  background: 'color-mix(in srgb, var(--m-fg) 7%, transparent)',
                  border: '1px solid var(--m-line)',
                  borderRadius: 'var(--m-radius-sm)', padding: '1px 6px',
                }}>{formatKeyList(entry, platform)}</code>
              </div>
            ))}
          </div>
        ))}
      </div>
    </FloatingWindow>
  )
}
